import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Upload } from 'lucide-react'
import Button from '../ui/Button'
import AdminFormField, { adminInputClass } from './AdminFormField'
import { previewImageSrc } from '../../utils/imagePreview'

export function useAdminImageField() {
  const fileRef = useRef(null)

  const createState = () => ({
    file: null,
    preview: null,
    reset() {
      if (this.preview?.startsWith('blob:')) {
        URL.revokeObjectURL(this.preview)
      }
      this.file = null
      this.preview = null
      if (fileRef.current) fileRef.current.value = ''
    },
    handleSelect(file, setPreview) {
      if (!file) return
      if (setPreview?.startsWith('blob:')) {
        URL.revokeObjectURL(setPreview)
      }
      this.file = file
      const preview = URL.createObjectURL(file)
      this.preview = preview
      return preview
    },
  })

  return { fileRef }
}

export default function AdminImageField({
  label,
  htmlFor,
  value = '',
  onChange,
  imageFile = null,
  imagePreview = null,
  onFileSelect,
  onRemove,
  disabled = false,
  required = false,
  className = '',
  showUrlInput = true,
}) {
  const { t } = useTranslation()
  const fileRef = useRef(null)
  const preview = previewImageSrc(value, imagePreview)

  const handleSelect = (e) => {
    const file = e.target.files?.[0]
    if (file && onFileSelect) onFileSelect(file)
  }

  return (
    <AdminFormField label={label} htmlFor={htmlFor} className={className}>
      <div className="space-y-3">
        {preview ? (
          <div>
            <p className="mb-2 text-xs text-text/60">{t('admin.slidersAdmin.imagePreview')}</p>
            <img
              src={preview}
              alt=""
              className="h-40 w-full max-w-md rounded-xl border border-border object-cover"
              onError={(e) => {
                e.currentTarget.style.display = 'none'
              }}
            />
          </div>
        ) : null}
        {showUrlInput ? (
          <input
            id={htmlFor}
            value={value || ''}
            onChange={(e) => onChange?.(e.target.value)}
            placeholder={t('admin.slidersAdmin.imageUrl')}
            required={required && !preview && !imageFile}
            className={adminInputClass}
          />
        ) : (
          <input id={htmlFor} type="hidden" value={value || ''} readOnly />
        )}
        <div className="flex flex-wrap items-center gap-3">
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleSelect}
          />
          <Button
            type="button"
            variant="outlineDark"
            size="sm"
            onClick={() => fileRef.current?.click()}
            disabled={disabled}
          >
            <Upload className="h-4 w-4" />
            {imageFile ? imageFile.name : t('admin.slidersAdmin.uploadImage')}
          </Button>
          {onRemove && (preview || value) ? (
            <Button type="button" variant="ghost" size="sm" onClick={onRemove} disabled={disabled}>
              {t('admin.pages.removeHeroImage')}
            </Button>
          ) : null}
        </div>
      </div>
    </AdminFormField>
  )
}
